<template>
    <DialogRoot
        v-model:open="open"
        :modal="false"
    >
        <slot
            name="trigger"
            :open="openModal"
        />

        <DialogPortal>
            <Transition name="mp-overlay">
                <div
                    v-if="showOverlay && open"
                    class="fixed inset-0 z-50 bg-gray-500/75"
                    @click.self="onOverlayClick"
                />
            </Transition>

            <Transition :name="transitionName">
                <DialogContent
                    :class="contentClasses"
                    @escape-key-down="open = false"
                    @pointer-down-outside="(e: any) => e.preventDefault()"
                    @interact-outside="(e: any) => e.preventDefault()"
                >
                    <div class="flex min-h-0 flex-1 flex-col">
                        <!-- Header -->
                        <div class="flex shrink-0 items-center border-b px-4 py-3 sm:px-6">
                            <button
                                v-if="closeButton"
                                type="button"
                                class="mr-4 flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100 focus:outline-none md:hidden"
                                @click="open = false"
                            >
                                <SvgIcon
                                    name="system-forward"
                                    class="h-5 w-5 rotate-180 text-gray-900"
                                />
                            </button>

                            <slot
                                name="header"
                                :form-id="formId"
                            >
                                <DialogTitle
                                    v-if="title"
                                    as="h3"
                                    class="text-lg font-medium"
                                >
                                    {{ title }}
                                </DialogTitle>
                            </slot>

                            <DialogClose
                                v-if="closeButton"
                                class="ml-auto hidden h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100 focus:outline-none md:flex"
                            >
                                <SvgIcon
                                    name="system-close"
                                    class="h-5 w-5 text-gray-900"
                                />
                            </DialogClose>
                        </div>

                        <!-- Sub-header (persistent, sits below the header) -->
                        <div
                            v-if="hasSubHeaderSlot"
                            class="shrink-0 border-b px-4 py-3 sm:px-6"
                        >
                            <slot
                                name="sub-header"
                                :form-id="formId"
                            />
                        </div>

                        <!-- Scrollable content -->
                        <div class="flex-1 overflow-y-auto px-4 py-4 [scrollbar-gutter:stable] sm:px-6">
                            <slot :form-id="formId" />
                        </div>

                        <!-- Footer -->
                        <div
                            v-if="hasFooterSlot"
                            class="shrink-0 px-4 py-3 sm:px-6"
                        >
                            <slot
                                name="footer"
                                :form-id="formId"
                            />
                        </div>
                    </div>
                </DialogContent>
            </Transition>
        </DialogPortal>
    </DialogRoot>
</template>

<script setup lang="ts">
import { DialogClose, DialogContent, DialogPortal, DialogRoot, DialogTitle } from 'reka-ui';

const props = withDefaults(
    defineProps<{
        variant?: 'center' | 'side';
        size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';
        title?: string;
        closeButton?: boolean;
        nonModal?: boolean;
        persistent?: boolean;
    }>(),
    {
        variant: 'center',
        size: 'lg',
        closeButton: true,
        nonModal: false,
        persistent: false,
    },
);

const open = defineModel<boolean>({ required: true });
const slots = useSlots();
const formId = useId();

const hasFooterSlot = computed(() => !!slots.footer);
const hasSubHeaderSlot = computed(() => !!slots['sub-header']);

const isNonModal = computed(() => props.nonModal || props.variant === 'side');
const showOverlay = computed(() => !isNonModal.value && props.variant === 'center');

const shouldLockScroll = computed(() => open.value && !isNonModal.value);
useScrollLock(computed(() => (shouldLockScroll.value ? document.body : null)));

function openModal() {
    open.value = true;
}

function onOverlayClick() {
    if (!props.persistent) {
        open.value = false;
    }
}

const sizeClass = computed(() => {
    const sizes: Record<string, string> = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        '2xl': 'max-w-2xl',
        '3xl': 'max-w-3xl',
        full: 'max-w-full',
    };
    return sizes[props.size] ?? 'max-w-lg';
});

const transitionName = computed(() => (props.variant === 'side' ? 'mp-slide' : 'mp-center'));

const contentClasses = computed(() => {
    const base = 'fixed inset-0 z-50 flex flex-col bg-white shadow-muval-3 focus:outline-none w-full';

    if (props.variant === 'side') {
        return [base, sizeClass.value, 'md:inset-4 md:left-auto md:rounded-muval-3'];
    }

    return [
        base,
        sizeClass.value,
        'md:right-auto md:bottom-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-h-[85vh] md:rounded-muval-2',
    ];
});
</script>

<style scoped>
/* Overlay fade */
.mp-overlay-enter-active,
.mp-overlay-leave-active {
    transition: opacity 0.3s ease;
}
.mp-overlay-enter-from,
.mp-overlay-leave-to {
    opacity: 0;
}

/* Center variant: fade + scale on desktop */
.mp-center-enter-active {
    transition:
        opacity 0.3s ease-out,
        transform 0.3s ease-out;
}
.mp-center-leave-active {
    transition:
        opacity 0.2s ease-in,
        transform 0.2s ease-in;
}
.mp-center-enter-from,
.mp-center-leave-to {
    opacity: 0;
    transform: scale(0.95);
}

@media (min-width: 768px) {
    .mp-center-enter-from,
    .mp-center-leave-to {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.95);
    }
}

/* Side variant: slide from right on desktop */
.mp-slide-enter-active {
    transition: transform 0.3s ease-out;
}
.mp-slide-leave-active {
    transition: transform 0.2s ease-in;
}
.mp-slide-enter-from,
.mp-slide-leave-to {
    transform: translateX(100%);
}

/* Mobile: both variants slide up from bottom */
@media (max-width: 767px) {
    .mp-center-enter-from,
    .mp-center-leave-to {
        opacity: 1;
        transform: translateY(100%);
    }

    .mp-slide-enter-from,
    .mp-slide-leave-to {
        transform: translateY(100%);
    }
}
</style>
